import { Controller } from "@hotwired/stimulus"

// 決定者リスト workspace の候補者パネル / エントリパネル用モード切替コントローラ
// モード: view (閲覧) / call (架電・候補者のみ) / bulk (一括編集)
export default class extends Controller {
  static values = {
    mode: { type: String, default: "view" },
    // 一括編集モードのサブモード ( "all" = 割り付け内全件一括、 "individual" = 個別選択 )
    bulkSubMode: { type: String, default: "all" },
    scope: String, // "candidates" or "entries"
    selectionListId: Number,
    projectId: Number,
    interviewDurationMinutes: Number
  }
  static targets = [
    "viewButton", "callButton", "bulkButton", "bulkButtonMessage",
    "exitCallButton",
    "callTalkScriptWrap", "callTalkScript",
    "headerView", "headerCall", "headerBulk",
    "bulkPanel", "bulkCount", "bulkCheckbox", "bulkSelectAll",
    "subModeGroup", "subModeAllButton", "subModeIndividualButton", "countAllMode", "countIndividualMode",
    "bulkContactStatus", "bulkMemo", "bulkConfirmedSchedule", "bulkConfirmedScheduleEnd",
    "bulkTestSchedule", "bulkVenueId", "bulkMeetingUrl", "bulkMeetingId", "bulkMeetingPasscode",
    "bulkOnlyCell", "nonBulkOnlyCell", "formCell", "formInput", "formContainer", "formText", "row",
    "lockOverlay", "lockMessage",
    "loadingOverlay", "loadingMessage"
  ]

  connect() {
    // データ 0 件などによる初期 disabled 状態を記憶
    this.initialBulkDisabled = this.hasBulkButtonTarget ? this.bulkButtonTarget.disabled : false
    this.updateUI()
    this.consumePendingFlash()
  }

  // リロードを跨いで持ち越されたトーストメッセージ ( 全件一括適用後 ) があれば表示する
  consumePendingFlash() {
    const raw = sessionStorage.getItem("list_mode_pending_flash")
    if (!raw) return
    sessionStorage.removeItem("list_mode_pending_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this.showFlash(message, type)
    } catch (_e) {
      // ignore
    }
  }

  // 右上に浮かぶトースト通知。 alert より邪魔にならない ( toast_controller で自動フェード )
  showFlash(message, type = "success") {
    const colorClass = type === "warning"
      ? "bg-amber-50 border-amber-300 text-amber-900"
      : type === "error"
        ? "bg-red-50 border-red-300 text-red-900"
        : "bg-emerald-50 border-emerald-300 text-emerald-900"
    const toast = document.createElement("div")
    toast.className = `fixed top-6 right-6 z-[100] rounded-lg border px-4 py-3 shadow-lg transition-opacity duration-300 max-w-sm ${colorClass}`
    toast.dataset.controller = "toast"
    toast.dataset.toastDurationValue = "4000"
    const lines = message.split("\n").filter(l => l !== "").map(l => `<div>${this.escapeHtml(l)}</div>`).join("")
    toast.innerHTML = `<div class="text-sm font-medium space-y-0.5">${lines}</div>`
    document.body.appendChild(toast)
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }

  disconnect() {
    if (this.modeValue === "bulk") {
      this.broadcastExitBulk()
    }
  }

  setView() {
    const prev = this.modeValue
    this.modeValue = "view"
    if (prev === "bulk") {
      this.broadcastExitBulk()
      this.clearSelectedIds()
    }
  }

  setCall() {
    if (this.scopeValue !== "candidates") return
    const prev = this.modeValue
    this.modeValue = "call"
    if (prev === "bulk") {
      this.broadcastExitBulk()
      this.clearSelectedIds()
    }
  }

  async setBulk() {
    this.modeValue = "bulk"
    this.bulkSubModeValue = "all" // 一括編集開始時は 「割り付け内全件一括」 に既定
    this.broadcastEnterBulk()
    // 「割り付け内全件一括」 モードでは checkbox 不要、 Set もクリアしておく
    this.clearSelectedIds()
  }

  setBulkSubModeAll() {
    if (this.modeValue !== "bulk") return
    this.bulkSubModeValue = "all"
    this.clearSelectedIds()
    this.bulkCheckboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasBulkSelectAllTarget) this.bulkSelectAllTarget.checked = false
    this.updateUI()
    this.updateBulkCount()
  }

  setBulkSubModeIndividual() {
    if (this.modeValue !== "bulk") return
    this.bulkSubModeValue = "individual"
    // 個別選択切替時は初期状態を全解除にし、 ユーザーがチェックした対象だけを送るようにする。
    this.clearSelectedIds()
    this.bulkCheckboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasBulkSelectAllTarget) this.bulkSelectAllTarget.checked = false
    this.updateUI()
    this.updateBulkCount()
  }

  // 一括編集モードに入ったときに表示中の全チェックボックスを ON にし、 Set にも追加する
  checkAllBulkCheckboxes() {
    const ids = this.ensureSelectedIds()
    this.bulkCheckboxTargets.forEach(cb => {
      cb.checked = true
      ids.add(cb.value)
    })
    if (this.hasBulkSelectAllTarget) this.bulkSelectAllTarget.checked = true
    this.updateBulkCount()
  }

  // 現在の検索 ( q ) / 割り付け ( segment ) 条件に一致する全候補者 ID を backend から取得して Set に入れる。
  // 表示中の checkbox は Set に基づいて checked を復元する。
  async preselectAllCandidates() {
    const urlParams = new URLSearchParams(window.location.search)
    const qs = new URLSearchParams()
    if (urlParams.get("q")) qs.set("q", urlParams.get("q"))
    if (urlParams.get("segment")) qs.set("segment", urlParams.get("segment"))
    const url = `/admin/projects/${this.projectIdValue}/selection_lists/${this.selectionListIdValue}/entries/candidate_ids${qs.toString() ? "?" + qs.toString() : ""}`
    try {
      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!response.ok) throw new Error("failed")
      const data = await response.json()
      const ids = this.ensureSelectedIds()
      data.candidate_ids.forEach(id => ids.add(String(id)))
      this.bulkCheckboxTargets.forEach(cb => { if (ids.has(cb.value)) cb.checked = true })
      if (this.hasBulkSelectAllTarget) this.bulkSelectAllTarget.checked = true
      this.updateBulkCount()
    } catch (e) {
      console.error("Failed to preselect candidates across pagination:", e)
      // フォールバック: 表示中の checkbox だけ ON にする ( 従来動作 )
      this.checkAllBulkCheckboxes()
    }
  }

  exitCall() {
    this.modeValue = "view"
  }

  // サーバーから拒否されたときなど、broadcast せずにデフォルトモードに戻す
  forceExitBulk() {
    this.modeValue = this.defaultMode()
  }

  // 一括編集モードからデフォルトモードに戻す（broadcast あり）
  exitBulkToDefault() {
    if (this.scopeValue === "candidates") {
      this.setCall()
    } else {
      this.setView()
    }
  }

  defaultMode() {
    return this.scopeValue === "candidates" ? "call" : "view"
  }

  // データ件数の変動で初期 disabled 状態を更新
  setBulkButtonInitialDisabled(disabled) {
    this.initialBulkDisabled = !!disabled
    if (this.hasBulkButtonTarget && !this.bulkButtonTarget.classList.contains("list-mode-button-active")) {
      // ロック中（他ユーザー編集中）でなければそのまま反映
      if (this.bulkButtonTarget.title === "") {
        this.bulkButtonTarget.disabled = !!disabled
      }
    }
  }

  // 他ユーザー編集中: 一括編集モードボタンを非活性化（ロック表示）
  setBulkBlocker(userName) {
    if (this.hasBulkButtonTarget) {
      this.bulkButtonTarget.disabled = true
      this.bulkButtonTarget.classList.add("bg-amber-100", "text-amber-700", "ring-1", "ring-amber-300", "cursor-not-allowed")
      this.bulkButtonTarget.classList.remove("hover:bg-gray-100", "text-gray-700")
      this.bulkButtonTarget.title = `${userName} が編集中のため一括編集モードに切り替えられません`
      this.showLockIcon(true)
    }
    if (this.hasBulkButtonMessageTarget) {
      this.bulkButtonMessageTarget.textContent = `${userName} が編集中のため一括編集モードにできません`
      this.bulkButtonMessageTarget.classList.remove("hidden")
    }
  }

  clearBulkBlocker() {
    if (this.hasBulkButtonTarget) {
      this.bulkButtonTarget.disabled = this.initialBulkDisabled || false
      this.bulkButtonTarget.classList.remove("bg-amber-100", "text-amber-700", "ring-1", "ring-amber-300", "cursor-not-allowed")
      this.bulkButtonTarget.classList.add("hover:bg-gray-100", "text-gray-700")
      this.bulkButtonTarget.title = ""
      this.showLockIcon(false)
    }
    if (this.hasBulkButtonMessageTarget) {
      this.bulkButtonMessageTarget.textContent = ""
      this.bulkButtonMessageTarget.classList.add("hidden")
    }
  }

  showLockIcon(show) {
    if (!this.hasBulkButtonTarget) return
    let icon = this.bulkButtonTarget.querySelector("[data-lock-icon]")
    if (show && !icon) {
      icon = document.createElement("span")
      icon.dataset.lockIcon = "true"
      icon.className = "inline-flex mr-1 align-middle"
      icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/></svg>`
      this.bulkButtonTarget.insertBefore(icon, this.bulkButtonTarget.firstChild)
    } else if (!show && icon) {
      icon.remove()
    }
  }

  modeValueChanged() {
    this.updateUI()
  }

  bulkSubModeValueChanged() {
    this.updateUI()
  }

  // 割り付け切替などで新しい行が動的に追加されたとき、現在のモードを再適用
  rowTargetConnected(row) {
    if (this.modeValue === "call" && row.dataset.rowLocked !== "true") {
      row.classList.add("cursor-pointer", "hover:bg-indigo-50")
    }
  }

  formInputTargetConnected(el) {
    const row = el.closest("tr")
    const confirmed = row && row.dataset.confirmedGrayed === "true"
    const isStatus = el.matches("[data-workspace-candidates-target='contactStatus']")
    // 確定行はステータス以外を hidden、未確定行は常に visible
    el.classList.toggle("hidden", confirmed && !isStatus)
  }

  formTextTargetConnected(el) {
    const cell = el.closest("[data-list-mode-target~='formCell']")
    const row = (cell || el).closest("tr")
    const confirmed = row && row.dataset.confirmedGrayed === "true"
    const hasStatusInput = cell && cell.querySelector("[data-workspace-candidates-target='contactStatus']") != null
    // 確定行ではステータスセル以外をテキスト表示。未確定行は常に hidden。
    el.classList.toggle("hidden", !(confirmed && !hasStatusInput))
    // 値も同期
    if (cell) {
      const input = cell.querySelector("[data-list-mode-target~='formInput']")
      if (input) el.textContent = this.formatValue(input) || "-"
    }
  }

  bulkOnlyCellTargetConnected(el) {
    // 一括編集モード + 個別選択 サブモード時のみ表示
    const shouldShow = this.modeValue === "bulk" && this.bulkSubModeValue === "individual"
    el.classList.toggle("hidden", !shouldShow)
  }

  nonBulkOnlyCellTargetConnected(el) {
    el.classList.toggle("hidden", this.modeValue === "bulk")
  }

  formCellTargetConnected(el) {
    // 一括編集モード中は行内フォームを操作させない
    const isBulk = this.modeValue === "bulk"
    el.classList.toggle("pointer-events-none", isBulk)
    el.classList.toggle("opacity-60", isBulk)
  }

  updateUI() {
    const mode = this.modeValue
    const isBulk = mode === "bulk"
    const isIndividualBulk = isBulk && this.bulkSubModeValue === "individual"

    // ボタン見た目
    if (this.hasViewButtonTarget) this.setButtonActive(this.viewButtonTarget, mode === "view")
    if (this.hasCallButtonTarget) this.setButtonActive(this.callButtonTarget, mode === "call")
    if (this.hasBulkButtonTarget) this.setButtonActive(this.bulkButtonTarget, isBulk)

    // 一括編集サブモードタブ全体 : bulk モード時のみ表示
    if (this.hasSubModeGroupTarget) this.subModeGroupTarget.classList.toggle("hidden", !isBulk)
    if (this.hasSubModeAllButtonTarget) this.setButtonActive(this.subModeAllButtonTarget, isBulk && this.bulkSubModeValue === "all")
    if (this.hasSubModeIndividualButtonTarget) this.setButtonActive(this.subModeIndividualButtonTarget, isIndividualBulk)

    // 件数表記の切替
    if (this.hasCountAllModeTarget) this.countAllModeTarget.classList.toggle("hidden", !(isBulk && this.bulkSubModeValue === "all"))
    if (this.hasCountIndividualModeTarget) this.countIndividualModeTarget.classList.toggle("hidden", !isIndividualBulk)

    // チェックボックス列 : 「個別選択」 サブモードでのみ表示 ( 「全件一括」 では意味がないため非表示 )
    this.bulkOnlyCellTargets.forEach(el => el.classList.toggle("hidden", !isIndividualBulk))

    // 一括編集モード以外で表示するセル（個別操作ボタン列など）
    this.nonBulkOnlyCellTargets.forEach(el => el.classList.toggle("hidden", isBulk))

    // フォーム列（td/th）: 常時表示。 一括編集モード中は行内フォームを操作させず 一括編集パネル
    // からのみ編集を受け付ける ( pointer-events-none + 透過で視覚フィードバック )。
    this.formCellTargets.forEach(el => {
      el.classList.remove("hidden")
      el.classList.toggle("pointer-events-none", isBulk)
      el.classList.toggle("opacity-60", isBulk)
    })

    // NOTE: 確定済み行 / 参加辞退行はステータス select 以外をテキスト表示に固定。
    //       未確定行は list-mode に関わらず常にフォーム入力可能 ( その場で編集できる ) にする。
    // data-confirmed-grayed は HAML 側でセットされるが、 ステータス select 値からも動的に判定して
    // ユーザー操作直後 ( ステータスを 参加辞退 に変えたとき等 ) にも即座にロックする。
    const isStatusInput = (el) => el.matches("[data-workspace-candidates-target='contactStatus']")
    const isStatusCell = (cell) => cell && cell.querySelector("[data-workspace-candidates-target='contactStatus']") != null
    const rowLocked = (row) => {
      if (!row) return false
      if (row.dataset.confirmedGrayed === "true") return true
      const select = row.querySelector("[data-workspace-candidates-target='contactStatus']")
      return select && select.value === "declined_participation"
    }

    this.formInputTargets.forEach(el => {
      const locked = rowLocked(el.closest("tr"))
      // ロック行はステータス以外を hidden、非ロック行は常に visible
      el.classList.toggle("hidden", locked && !isStatusInput(el))
    })

    this.formContainerTargets.forEach(el => {
      const locked = rowLocked(el.closest("tr"))
      // 分割 UI ラッパー ( datetime_field 等 ): ロック行のみ非表示
      el.classList.toggle("hidden", locked)
    })

    this.formTextTargets.forEach(el => {
      const cell = el.closest("[data-list-mode-target~='formCell']")
      const locked = rowLocked((cell || el).closest("tr"))
      // ロック行ではステータスセル以外をテキスト表示。 非ロック行は常に hidden。
      el.classList.toggle("hidden", !(locked && !isStatusCell(cell)))
    })

    // テキスト表示の内容を現在の入力値から更新
    this.syncFormText()

    // 一括適用パネル
    if (this.hasBulkPanelTarget) this.bulkPanelTarget.classList.toggle("hidden", mode !== "bulk")

    // 架電モード終了ボタン
    if (this.hasExitCallButtonTarget) this.exitCallButtonTarget.classList.toggle("hidden", mode !== "call")

    // 架電モード時のトークスクリプト選択
    if (this.hasCallTalkScriptWrapTarget) this.callTalkScriptWrapTarget.classList.toggle("hidden", mode !== "call")

    // モード別ヘッダの表示切替
    this.toggleHeader(this.hasHeaderViewTarget && this.headerViewTarget, mode === "view", "flex")
    this.toggleHeader(this.hasHeaderCallTarget && this.headerCallTarget, mode === "call", "flex")
    this.toggleHeader(this.hasHeaderBulkTarget && this.headerBulkTarget, mode === "bulk", "flex")

    // 行のカーソルとホバー（ロック中の行は除外）
    this.rowTargets.forEach(row => {
      if (row.dataset.rowLocked === "true") return
      row.classList.toggle("cursor-pointer", mode === "call")
      row.classList.toggle("hover:bg-indigo-50", mode === "call")
    })

    // bulk モードを抜けたときチェックボックスをリセット
    if (mode !== "bulk") {
      this.bulkCheckboxTargets.forEach(cb => { cb.checked = false })
      if (this.hasBulkSelectAllTarget) this.bulkSelectAllTarget.checked = false
      this.updateBulkCount()
    }
  }

  syncFormText() {
    // 各 formCell 内の formInput から値を読み取り、対応する formText に反映
    this.formCellTargets.forEach(cell => {
      const input = cell.querySelector("[data-list-mode-target~='formInput']")
      const text = cell.querySelector("[data-list-mode-target~='formText']")
      if (!input || !text) return
      text.textContent = this.formatValue(input) || "-"
    })
  }

  formatValue(input) {
    const v = input.value
    if (v === "" || v == null) return ""
    // datetime-local input または同形式の hidden input (分割 UI partial 経由)
    if (input.type === "datetime-local" || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v)
      if (isNaN(d)) return v
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      const hh = String(d.getHours()).padStart(2, "0")
      const mm = String(d.getMinutes()).padStart(2, "0")
      return `${y}/${m}/${day} ${hh}:${mm}`
    }
    if (input.type === "number") {
      const n = parseInt(v, 10)
      if (isNaN(n)) return v
      return n.toLocaleString()
    }
    if (input.tagName === "SELECT") {
      const opt = input.options[input.selectedIndex]
      return opt && opt.value ? opt.text : ""
    }
    return v
  }

  toggleHeader(el, show, displayClass) {
    if (!el) return
    if (show) {
      el.classList.remove("hidden")
      el.classList.add(displayClass)
    } else {
      el.classList.add("hidden")
      el.classList.remove(displayClass)
    }
  }

  setButtonActive(btn, active) {
    // サブモードタブは 「sub-mode-button-active」 、 それ以外は 「list-mode-button-active」 を切り替える
    const activeClass = btn.classList.contains("sub-mode-button") ? "sub-mode-button-active" : "list-mode-button-active"
    btn.classList.toggle(activeClass, active)
  }

  // チェックボックス列など、行クリック (openRow) を発火させたくない要素用
  stopRowOpen(event) {
    event.stopPropagation()
  }

  // 架電モードで行がクリックされたとき
  openRow(event) {
    if (this.modeValue !== "call") return
    const row = event.currentTarget
    // ロック中の行はクリック無効
    if (row.dataset.rowLocked === "true") return
    // フォーム要素内のクリックは無視
    if (event.target.closest("input, button, select, a, label, [contenteditable]")) return
    let url = row.dataset.rowModalUrl
    const candidateId = row.dataset.candidateId
    if (!url) return

    // 架電モードで選択されたトークスクリプトを URL に付加
    const tsId = this.hasCallTalkScriptTarget ? this.callTalkScriptTarget.value : null
    if (tsId) {
      const u = new URL(url, window.location.origin)
      u.searchParams.set("talk_script_id", tsId)
      url = u.pathname + u.search
    }

    const modalEl = document.querySelector("[data-controller~='candidate-modal']")
    if (!modalEl) return
    const modal = this.application.getControllerForElementAndIdentifier(modalEl, "candidate-modal")
    if (modal && modal.openUrl) modal.openUrl(url, candidateId)
  }

  // NOTE: ページネーションを跨いだ選択を維持するため、 チェック状態は個別 checkbox の checked プロパティに
  //       頼らず、 コントローラ側の Set ( selectedIds ) を single source of truth にする。
  //       ページ遷移 ( turbo frame reload ) で checkbox が再生成されるたびに Connected コールバックで
  //       Set の状態を復元する。
  ensureSelectedIds() {
    if (!this.selectedIds) this.selectedIds = new Set()
    return this.selectedIds
  }

  bulkCheckboxTargetConnected(el) {
    if (this.modeValue !== "bulk") return
    if (this.ensureSelectedIds().has(el.value)) el.checked = true
  }

  toggleBulkSelectAll(event) {
    const checked = event.target.checked
    const ids = this.ensureSelectedIds()
    this.bulkCheckboxTargets.forEach(cb => {
      cb.checked = checked
      if (checked) ids.add(cb.value)
      else ids.delete(cb.value)
    })
    this.updateBulkCount()
  }

  bulkCheckboxChanged(event) {
    const cb = event.target
    const ids = this.ensureSelectedIds()
    if (cb.checked) ids.add(cb.value)
    else ids.delete(cb.value)
    this.updateBulkCount()
  }

  updateBulkCount() {
    const size = this.ensureSelectedIds().size
    if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = size
    // 全選択チェックボックスの状態: 表示中のチェックボックスが全て ON かで判定
    if (this.hasBulkSelectAllTarget) {
      const visible = this.bulkCheckboxTargets
      const total = visible.length
      const checkedInView = visible.filter(cb => cb.checked).length
      this.bulkSelectAllTarget.checked = total > 0 && checkedInView === total
    }
  }

  clearSelectedIds() {
    this.selectedIds = new Set()
  }

  buildBulkAttrsBody() {
    const body = {
      memo: this.hasBulkMemoTarget ? this.bulkMemoTarget.value : "",
      confirmed_schedule: this.hasBulkConfirmedScheduleTarget ? this.bulkConfirmedScheduleTarget.value : "",
      confirmed_schedule_end: this.hasBulkConfirmedScheduleEndTarget ? this.bulkConfirmedScheduleEndTarget.value : "",
      test_schedule: this.hasBulkTestScheduleTarget ? this.bulkTestScheduleTarget.value : "",
      venue_id: this.hasBulkVenueIdTarget ? this.bulkVenueIdTarget.value : "",
      meeting_url: this.hasBulkMeetingUrlTarget ? this.bulkMeetingUrlTarget.value : "",
      meeting_id: this.hasBulkMeetingIdTarget ? this.bulkMeetingIdTarget.value : "",
      meeting_passcode: this.hasBulkMeetingPasscodeTarget ? this.bulkMeetingPasscodeTarget.value : ""
    }
    // ステータスは 「(変更しない)」 = __keep__ が既定。 明示的に選ばれたときだけ body に載せる ( "" は 未連絡 = 明示的入力 )
    if (this.hasBulkContactStatusTarget) {
      const v = this.bulkContactStatusTarget.value
      if (v !== "__keep__") body.contact_status = v
    }
    return body
  }

  hasAnyBulkAttrs(body) {
    // contact_status キーが body に含まれていれば 有効入力 ( "" = 未連絡 も含む )
    if (Object.prototype.hasOwnProperty.call(body, "contact_status")) return true
    return Object.values(body).some(v => v && v !== "")
  }

  async applyBulk() {
    if (this.modeValue !== "bulk") return
    if (this.bulkSubModeValue === "all") {
      await this.applyBulkAll()
    } else {
      await this.applyBulkIndividual()
    }
  }

  async applyBulkAll() {
    const body = this.buildBulkAttrsBody()
    if (!this.hasAnyBulkAttrs(body)) {
      alert("適用する項目を少なくとも 1 つ入力してください")
      return
    }
    const confirmMsg = this.scopeValue === "candidates"
      ? "現在の割り付けに一致する候補者すべてに一括適用します。 よろしいですか？"
      : "決定者リスト内の全員に一括適用します。 よろしいですか？"
    if (!confirm(confirmMsg)) return

    let url
    if (this.scopeValue === "candidates") {
      const urlParams = new URLSearchParams(window.location.search)
      body.q = urlParams.get("q") || ""
      body.segment = urlParams.get("segment") || ""
      url = `/admin/projects/${this.projectIdValue}/selection_lists/${this.selectionListIdValue}/entries/bulk_apply_all`
    } else {
      // 決定者リストパネル ( entries scope ) は 表示中の全 entry_ids を送って bulk_update に流す
      body.entry_ids = this.bulkCheckboxTargets.map(cb => cb.value)
      if (body.entry_ids.length === 0) {
        alert("適用対象がありません")
        return
      }
      url = `/admin/projects/${this.projectIdValue}/selection_lists/${this.selectionListIdValue}/entries/bulk_update`
    }
    this.showLoadingOverlay("一括適用中です")
    // candidates scope は影響件数が大きいのでリロードで整合、 entries scope は cable ブロードキャスト任せ
    await this.sendBulkRequest(url, body, { reloadOnSuccess: this.scopeValue === "candidates" })
  }

  async applyBulkIndividual() {
    const selected = Array.from(this.ensureSelectedIds())
    if (selected.length === 0) {
      alert("対象を選択してください")
      return
    }
    const body = this.buildBulkAttrsBody()
    let url
    if (this.scopeValue === "candidates") {
      url = `/admin/projects/${this.projectIdValue}/selection_lists/${this.selectionListIdValue}/entries/bulk_save_draft`
      body.candidate_ids = selected
    } else {
      url = `/admin/projects/${this.projectIdValue}/selection_lists/${this.selectionListIdValue}/entries/bulk_update`
      body.entry_ids = selected
    }
    this.showLoadingOverlay("一括適用中です")
    await this.sendBulkRequest(url, body, { reloadOnSuccess: false })
  }

  async sendBulkRequest(url, body, { reloadOnSuccess }) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        this.hideLoadingOverlay()
        alert("一括適用に失敗しました")
        return
      }

      const data = await response.json()
      let msg = `${data.applied}件に適用しました`
      if (data.skipped > 0) {
        const names = (data.skipped_names || []).slice(0, 3).join("、")
        const more = data.skipped > 3 ? ` 他${data.skipped - 3}名` : ""
        msg += `\n\n${data.skipped}件は他ユーザーが編集中のためスキップされました:\n${names}${more}`
      }
      if (reloadOnSuccess) {
        // NOTE: 全件一括は影響件数が大きく Cable ブロードキャストだけでは追いつかないためリロードで整合を取る
        // リロード後にトースト表示できるよう sessionStorage 経由でメッセージを引き継ぐ
        sessionStorage.setItem("list_mode_pending_flash", JSON.stringify({ message: msg, type: "success" }))
        location.reload()
      } else {
        this.hideLoadingOverlay()
        this.showFlash(msg, "success")
        // フォーム / 選択状態リセットして 一括編集モード継続
        this.resetBulkPanel()
      }
    } catch (error) {
      console.error("Bulk apply failed:", error)
      this.hideLoadingOverlay()
      alert("一括適用に失敗しました")
    }
  }

  showLoadingOverlay(message) {
    if (this.hasLoadingMessageTarget && message) this.loadingMessageTarget.textContent = message
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.classList.remove("hidden")
  }

  hideLoadingOverlay() {
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.classList.add("hidden")
  }

  resetBulkPanel() {
    // フォーム値リセット ( ステータスは 「(変更しない)」 に戻す )
    if (this.hasBulkContactStatusTarget) this.bulkContactStatusTarget.value = "__keep__"
    if (this.hasBulkMemoTarget) this.bulkMemoTarget.value = ""
    if (this.hasBulkVenueIdTarget) this.bulkVenueIdTarget.value = ""
    if (this.hasBulkMeetingUrlTarget) this.bulkMeetingUrlTarget.value = ""
    if (this.hasBulkMeetingIdTarget) this.bulkMeetingIdTarget.value = ""
    if (this.hasBulkMeetingPasscodeTarget) this.bulkMeetingPasscodeTarget.value = ""
    this.resetDatetimeTarget(this.hasBulkConfirmedScheduleTarget ? this.bulkConfirmedScheduleTarget : null)
    this.resetDatetimeTarget(this.hasBulkConfirmedScheduleEndTarget ? this.bulkConfirmedScheduleEndTarget : null)
    this.resetDatetimeTarget(this.hasBulkTestScheduleTarget ? this.bulkTestScheduleTarget : null)
    // チェックボックス / 選択 Set リセット
    this.clearSelectedIds()
    this.bulkCheckboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasBulkSelectAllTarget) this.bulkSelectAllTarget.checked = false
    this.updateBulkCount()
  }

  resetDatetimeTarget(target) {
    if (!target) return
    target.value = ""
    const wrapper = target.closest("[data-controller~='datetime-picker']")
    if (!wrapper) return
    const dpCtrl = this.application.getControllerForElementAndIdentifier(wrapper, "datetime-picker")
    if (dpCtrl && typeof dpCtrl.setValue === "function") dpCtrl.setValue("")
  }

  // 一括パネルの開始時間変更 → 終了時間の日付を同期し、インタビュー時間が設定されていれば終了時刻も自動計算
  autoFillBulkEndTime(event) {
    const startValue = event.target.value
    if (!startValue || !this.hasBulkConfirmedScheduleEndTarget) return
    const endHidden = this.bulkConfirmedScheduleEndTarget
    const wrapper = endHidden.closest("[data-controller~='datetime-picker']")
    if (!wrapper) return

    // time_only モード: date hidden input に開始と同じ日付をセット
    const endDateInput = wrapper.querySelector("[data-datetime-picker-target='date']")
    if (endDateInput && endDateInput.type === "hidden") {
      endDateInput.value = startValue.split("T")[0]
    }

    if (!this.interviewDurationMinutesValue) return
    const endValue = this.addMinutesToDatetimeLocal(startValue, this.interviewDurationMinutesValue)
    const dpCtrl = this.application.getControllerForElementAndIdentifier(wrapper, "datetime-picker")
    if (dpCtrl && typeof dpCtrl.setValue === "function") dpCtrl.setValue(endValue)
  }

  addMinutesToDatetimeLocal(datetimeLocal, minutes) {
    const [datePart, timePart] = datetimeLocal.split("T")
    const [year, month, day] = datePart.split("-").map(Number)
    const [hour, minute] = timePart.split(":").map(Number)
    const d = new Date(year, month - 1, day, hour, minute + minutes)
    const pad = (n) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  broadcastEnterBulk() {
    const ch = this.findChannelController()
    if (ch && ch.enterBulkMode) ch.enterBulkMode(this.scopeValue)
  }

  broadcastExitBulk() {
    const ch = this.findChannelController()
    if (ch && ch.exitBulkMode) ch.exitBulkMode(this.scopeValue)
  }

  // workspace-channel から呼ばれる
  applyLockFromOther(userName) {
    if (this.hasLockOverlayTarget) this.lockOverlayTarget.classList.remove("hidden")
    if (this.hasLockMessageTarget) this.lockMessageTarget.textContent = `${userName} が一括編集中です`
    ;[this.viewButtonTarget, this.callButtonTarget, this.bulkButtonTarget, this.exitCallButtonTarget].forEach(btn => {
      if (btn) btn.disabled = true
    })
    // 自分が編集中でなければ bulk モードに強制的には入れない→ view に戻す
    if (this.modeValue !== "view") this.modeValue = "view"
  }

  clearLockFromOther() {
    if (this.hasLockOverlayTarget) this.lockOverlayTarget.classList.add("hidden")
    ;[this.viewButtonTarget, this.callButtonTarget, this.bulkButtonTarget, this.exitCallButtonTarget].forEach(btn => {
      if (btn) btn.disabled = false
    })
  }

  findChannelController() {
    const el = this.element.closest("[data-controller~='workspace-channel']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "workspace-channel")
  }
}
